/** Full-screen knowledge processing console shown while a document is validated. */
import React from "react";
import { Loader2 } from "lucide-react";
import { ConsoleShell } from "./ConsoleShell";
import { PipelineOrbit2D } from "./PipelineOrbit2D";
import {
  buildResultTone,
  usePipelineRun,
  type ConsolePhase,
} from "./processingPipeline";
import type { ConsoleRuleCounts } from "./utils";
import { IN_FLIGHT_PHASES } from "./constants";
import type {
  IngestStageEvent,
  ValidateFileResult,
} from "./api/contentRegistryApi";
import type {
  LayerKey,
  LayerRunState,
} from "../../../stores/processingJobStore";

export interface ProcessingConsoleProps {
  fileName?: string;
  fileSize?: string;
  documentTitle?: string;
  documentType?: string;
  phase: ConsolePhase;
  result?: ValidateFileResult | null;
  errorMessage?: string | null;
  onCancel?: () => void;
  /** Optional back-to-picker action forwarded to the console shell. */
  onBack?: () => void;
  /** Fires once the closing stages have played out. */
  onFinished?: () => void;
  topBarExtra?: React.ReactNode;
  notice?: { title: string; detail?: string } | null;
  footer?: React.ReactNode;
  onMinimize?: () => void;
  /** Duration the backing request took; paces the closing stages. */
  apiDurationMs?: number | null;
  /** Render in-flow (sidebar visible) instead of as an overlay. */
  inline?: boolean;
  onSave?: () => void;
  saving?: boolean;
  saveLabel?: string;
  /** Console mounted but hidden (minimised); run keeps ticking, canvas is parked. */
  paused?: boolean;
  onStageChange?: (stage: number) => void;
  /** Live per-stage state from the ingest SSE stream (keyed by backend stage name). */
  pipelineStageEvents?: Record<string, IngestStageEvent>;
  /** Per-layer classification run state; drives the seven-layer panel. */
  layerRuns?: Partial<Record<LayerKey, LayerRunState>>;
  /** Optional title override shown in the console top bar (e.g. "Preview Validation"). */
  variantLabel?: string;
  domainScopeKey?: string;
  onRuleCountsChange?: (value: {
    documentId: string | null;
    counts: ConsoleRuleCounts | null;
  }) => void;
}

export function ProcessingConsole({
  fileName,
  fileSize,
  documentTitle,
  documentType,
  phase,
  result = null,
  errorMessage = null,
  onCancel,
  onBack,
  onFinished,
  topBarExtra,
  notice,
  footer,
  onMinimize,
  apiDurationMs = null,
  inline,
  onSave,
  saving = false,
  saveLabel = "Save Document",
  paused = false,
  onStageChange,
  pipelineStageEvents,
  layerRuns,
  variantLabel,
  domainScopeKey,
  onRuleCountsChange,
}: ProcessingConsoleProps) {
  const run = usePipelineRun(
    phase,
    onFinished,
    apiDurationMs ?? result?.durationMs ?? null,
    result?.sevenLayerValidation?.clearedLayers ?? null,
  );

  const onStageChangeRef = React.useRef(onStageChange);
  onStageChangeRef.current = onStageChange;
  React.useEffect(() => {
    onStageChangeRef.current?.(run.stage);
  }, [run.stage]);

  const defaultFooter =
    onSave || onCancel ? (
      <div className="flex w-full items-center justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
        )}
        {onSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 disabled:opacity-40 cursor-pointer"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {saving ? "Saving & Uploading..." : saveLabel}
          </button>
        )}
      </div>
    ) : null;

  return (
    <ConsoleShell
      fileName={fileName}
      fileSize={fileSize}
      documentTitle={documentTitle}
      documentType={documentType}
      phase={phase}
      run={run}
      result={result}
      errorMessage={errorMessage}
      onCancel={onCancel}
      onBack={onBack}
      topBarExtra={topBarExtra}
      notice={notice}
      footer={footer !== undefined ? footer : defaultFooter}
      onMinimize={onMinimize}
      inline={inline}
      pipelineStageEvents={pipelineStageEvents}
      layerRuns={layerRuns}
      variantLabel={variantLabel}
      domainScopeKey={domainScopeKey}
      onRuleCountsChange={onRuleCountsChange}
      core={
        <PipelineOrbit2D
          run={run}
          paused={paused}
          settled={!!result}
          tone={buildResultTone(result, phase)}
          active={IN_FLIGHT_PHASES.includes(phase) && !run.done}
          layerRuns={layerRuns}
        />
      }
    />
  );
}
